import { useEffect, useState } from 'react';

import { fg } from '@confluence/feature-gating';

export type PageContentDimensions = {
	width: number | string;
	left?: number;
	right?: number;
};

/**
 * Custom hook to track page-content-only dimensions for layout alignment and blue background sizing
 * @returns Object containing width, left, and right dimensions of the page-content-only element
 */
export const usePageContentDimensions = (): PageContentDimensions => {
	const [pageContentWidth, setPageContentWidth] = useState<number>(0);
	const [pageContentLeft, setPageContentLeft] = useState<number>(0);
	const [pageContentRight, setPageContentRight] = useState<number>(0);

	useEffect(() => {
		// Early return if sticky footer feature is not enabled OR if expand footer editor is enabled
		// Will be cleaned up once expand footer editor is fully released
		if (
			!fg('confluence_comment_sticky_footer_enabled') ||
			fg('confluence_comment_expand_footer_editor')
		) {
			return;
		}
		const pageContentOnly = document.querySelector("[data-testid='page-content-only']");
		if (pageContentOnly) {
			const updateDimensions = () => {
				const rect = pageContentOnly.getBoundingClientRect();
				setPageContentWidth(rect.width);
				setPageContentLeft(rect.left);
				setPageContentRight(rect.right);
			};

			const resizeObserver = new ResizeObserver(() => {
				updateDimensions();
			});

			const handleWindowResize = () => {
				updateDimensions();
			};

			resizeObserver.observe(pageContentOnly);
			window.addEventListener('resize', handleWindowResize);

			const mutationObserver = new MutationObserver(() => {
				const newPageContentOnly = document.querySelector("[data-testid='page-content-only']");
				if (newPageContentOnly && newPageContentOnly !== pageContentOnly) {
					resizeObserver.disconnect();
					resizeObserver.observe(newPageContentOnly);
					updateDimensions();
				}
			});

			mutationObserver.observe(document.body, {
				childList: true,
				subtree: true,
			});

			updateDimensions();

			return () => {
				resizeObserver.disconnect();
				window.removeEventListener('resize', handleWindowResize);
				mutationObserver.disconnect();
			};
		}
	}, []);

	useEffect(() => {
		// Early return if sticky footer feature is not enabled OR if expand footer editor is not enabled
		if (
			!fg('confluence_comment_sticky_footer_enabled') ||
			!fg('confluence_comment_expand_footer_editor')
		) {
			return;
		}

		let resizeObserver: ResizeObserver | null = null;
		let mutationObserver: MutationObserver | null = null;
		let pageContentOnly: Element | null = null;

		const updateDimensions = () => {
			if (!pageContentOnly) return;
			const rect = pageContentOnly.getBoundingClientRect();
			setPageContentWidth(rect.width);
			setPageContentLeft(rect.left);
			setPageContentRight(rect.right);
		};

		const handleWindowResize = () => {
			updateDimensions();
		};

		// Set up window resize listener once (outside setupObservers to avoid duplicates)
		window.addEventListener('resize', handleWindowResize);

		const setupObservers = (element: Element) => {
			pageContentOnly = element;

			// Clean up existing ResizeObserver if any
			if (resizeObserver) {
				resizeObserver.disconnect();
			}

			// Set up ResizeObserver for the element
			resizeObserver = new ResizeObserver(() => {
				updateDimensions();
			});
			resizeObserver.observe(element);

			// Initial dimension calculation
			updateDimensions();
		};

		// Check if element already exists
		const existingElement = document.querySelector("[data-testid='page-content-only']");
		if (existingElement) {
			setupObservers(existingElement);
		}

		// Set up MutationObserver to watch for when element is rendered or replaced
		mutationObserver = new MutationObserver(() => {
			const newPageContentOnly = document.querySelector("[data-testid='page-content-only']");

			// If element exists and is different from current one, set up observers
			if (newPageContentOnly && newPageContentOnly !== pageContentOnly) {
				setupObservers(newPageContentOnly);
			}
		});

		mutationObserver.observe(document.body, {
			childList: true,
			subtree: true,
		});

		return () => {
			if (resizeObserver) {
				resizeObserver.disconnect();
			}
			window.removeEventListener('resize', handleWindowResize);
			if (mutationObserver) {
				mutationObserver.disconnect();
			}
		};
	}, []);

	return {
		width: fg('confluence_comment_sticky_footer_enabled') ? pageContentWidth : '100%',
		left: pageContentLeft,
		right: pageContentRight,
	};
};
